import { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom'; // Add this import

type LoadingContextType = {
    isLoading: boolean;
    bypassPaths: string[];
    setBypassPaths: (paths: string[]) => void;
};

const LoadingContext = createContext<LoadingContextType>({} as LoadingContextType);

type LoadingProviderProps = {
    children: ReactNode;
    instances: AxiosInstance[];
    initialBypass?: string[];
};

export const LoadingProvider = ({
    children,
    instances,
    initialBypass = []
}: LoadingProviderProps) => {

    const [count, setCount] = useState(0);
    const [bypassPaths, setBypassPaths] = useState<string[]>(initialBypass);
    const isLoading = count > 0;
    const location = useLocation();


    const shouldBypass = (config: InternalAxiosRequestConfig) => {
        return bypassPaths.some(path =>
            config.url?.startsWith(path) ||
            config.url?.includes(path)
        );
    };
    useEffect(() => {
        setBypassPaths(['/GetPropertyGroupScans', '/GetUserNotificationsByLocalId']);
    }, []);

    useEffect(() => {
        const interceptors: {
            instance: AxiosInstance;
            reqId: number;
            resId: number;
        }[] = [];

        instances.forEach(instance => {
            const reqInterceptor = instance.interceptors.request.use(
                config => {
                    if (!shouldBypass(config)) {
                        setCount(prev => prev + 1);
                    }
                    return config;
                },
                error => {
                    if (!shouldBypass(error.config)) {
                        setCount(prev => prev - 1);
                    }
                    return Promise.reject(error);
                }
            );

            const resInterceptor = instance.interceptors.response.use(
                response => {
                    if (!shouldBypass(response.config)) {
                        setCount(prev => prev - 1);
                    }
                    return response;
                },
                error => {
                    if (error.config && !shouldBypass(error.config)) {
                        setCount(prev => prev - 1);
                    }
                    return Promise.reject(error);
                }
            );

            interceptors.push({ instance, reqId: reqInterceptor, resId: resInterceptor });
        });


        return () => {
            interceptors.forEach(({ instance, reqId, resId }) => {
                instance.interceptors.request.eject(reqId);
                instance.interceptors.response.eject(resId);
            });
        };
    }, [instances, bypassPaths]);

    useEffect(() => {
        setCount(0);
    }, [location.pathname]);

    return (
        <LoadingContext.Provider value={{ isLoading, bypassPaths, setBypassPaths }}>
            {children}
        </LoadingContext.Provider>
    );
};

export const useLoading = () => useContext(LoadingContext);
